/**
 * Main-thread client for the sim worker. UI lanes talk to this, never to the
 * engine. Owns save persistence (localStorage): the worker builds/validates
 * envelopes, the bridge stores and retrieves them. Requests and responses
 * pair in order per message kind.
 */
import type { SimCommand, SimSnapshot, Speed } from '../contracts/sim';
import { SaveManager, LocalStorageAdapter, type SlotInfo, type StorageAdapter } from '../save/save-manager';
import { SaveFreshness, safeFlush } from '../save/save-freshness';
import type { OfflineCatchup } from '../save/offline-policy';
import type { MainToWorker, WorkerToMain } from './protocol';

interface WorkerLike {
  postMessage(msg: MainToWorker, transfer?: Transferable[]): void;
  addEventListener(type: 'message', fn: (e: { data: WorkerToMain }) => void): void;
  terminate(): void;
}

interface Waiter {
  kinds: readonly WorkerToMain['kind'][];
  resolve: (m: WorkerToMain) => void;
}

export class SimWorkerClient {
  private waiters: Waiter[] = [];
  private sessionGeneration = 0;
  /** Saving failure does not undo an already executed action. */
  onPersistenceError?: (error: unknown) => void;
  checkpointNow?: () => number;
  commandCheckpointEnabled = false;
  beforeRanchReplacement?: () => Promise<void>;
  private readonly saves: SaveManager;

  constructor(
    private readonly worker: WorkerLike,
    storage?: StorageAdapter,
  ) {
    this.saves = new SaveManager(storage ?? new LocalStorageAdapter());
    worker.addEventListener('message', (e) => { this.onMessage(e.data); });
  }

  private onMessage(msg: WorkerToMain): void {
    const i = this.waiters.findIndex((w) => w.kinds.includes(msg.kind));
    if (i < 0) return; // unsolicited message; drop
    const [waiter] = this.waiters.splice(i, 1);
    if (waiter) waiter.resolve(msg);
  }

  private next<K extends WorkerToMain['kind']>(kinds: readonly K[], timeoutMs = 10_000): Promise<Extract<WorkerToMain, { kind: K }>> {
    // Resilience (P029): a wedged worker must reject, never hang the caller
    // forever - the UI layer's recovery watches for exactly this.
    return new Promise((resolve, reject) => {
      const waiter: Waiter = { kinds, resolve: resolve as (m: WorkerToMain) => void };
      this.waiters.push(waiter);
      setTimeout(() => {
        const i = this.waiters.indexOf(waiter);
        if (i >= 0) {
          this.waiters.splice(i, 1);
          reject(new Error(`sim worker timeout after ${String(timeoutMs)}ms`));
        }
      }, timeoutMs);
    });
  }

  private send(msg: MainToWorker): void {
    this.worker.postMessage(msg);
  }

  init(seed: number): Promise<void> {
    // A new engine replaces the session outright: drop any cached envelopes
    // and pending-save sequences from a prior one (defense for in-place
    // resets; handle recreation already starts with a fresh guard).
    this.sessionGeneration += 1;
    this.freshness.invalidate();
    const ready = this.next(['ready', 'error']).then((m) => {
      if (m.kind === 'error') throw new Error(m.message);
    });
    this.send({ kind: 'init', seed });
    return ready;
  }

  /** Returns the bus-assigned command id; throws on boundary rejection. */
  async command(type: string, payload: unknown, source: SimCommand['source'], tick?: number): Promise<string> {
    if (type === 'story.begin') await this.beforeRanchReplacement?.();
    const generation = this.sessionGeneration;
    const wait = this.next(['ack', 'reject', 'error']);
    this.send({ kind: 'command', type, payload, source, ...(tick !== undefined ? { tick } : {}) });
    const res = await wait;
    if (res.kind === 'ack') {
      if (this.commandCheckpointEnabled && res.checkpoint !== undefined && generation === this.sessionGeneration) {
        try {
          // A received post-command checkpoint supersedes any older save
          // response still in flight. Persist before command success resolves.
          this.storeEnvelope(0, res.checkpoint, this.checkpointNow?.());
        } catch (error) { this.onPersistenceError?.(error); }
      }
      return res.commandId;
    }
    if (res.kind === 'reject') throw new Error(`command rejected: ${res.reason}`);
    throw new Error(res.message);
  }

  setSpeed(speed: Speed): void {
    this.send({ kind: 'setSpeed', speed });
  }

  async snapshot(): Promise<SimSnapshot> {
    const generation=this.sessionGeneration;
    const wait = this.next(['snapshot', 'error'], 5_000);
    this.send({ kind: 'snapshot' });
    const res = await wait;
    if (res.kind === 'snapshot') {
      if (this.commandCheckpointEnabled && res.checkpoint !== undefined && generation === this.sessionGeneration) {
        try { this.storeEnvelope(0,res.checkpoint,this.checkpointNow?.()); }
        catch(error) { this.onPersistenceError?.(error); }
      }
      return res.snapshot;
    }
    throw new Error(res.message);
  }

  /** Slot summaries for the load screen (null = empty slot; corrupt flagged). */
  listSlots(): SlotInfo[] {
    return this.saves.listSlots();
  }

  /** Promote a slot's one-deep backup to live (P129). Throws when none. */
  restoreBackup(slot: number): void {
    this.sessionGeneration += 1;
    this.freshness.invalidate();
    this.saves.restoreSlotBackup(slot);
  }

  /** Save live state into a local slot (worker builds, main stores).
   *  nowMs stamps the envelope for offline catch-up (P128). */
  async save(slot: number, nowMs?: number): Promise<number> {
    const seq = this.freshness.issue(slot);
    const wait = this.next(['stateData', 'error'], 30_000);
    this.send({ kind: 'save', slot });
    const res = await wait;
    if (res.kind === 'error') throw new Error(res.message);
    // Wall-clock stamp happens on the main thread: the worker never sees
    // Date.now (frozen sim contract), the envelope carries it instead.
    const stamped = nowMs !== undefined ? this.saves.stampEnvelope(res.envelope, nowMs) : res.envelope;
    // Out-of-order guard: a newer save was issued after this one - drop this
    // stale response entirely rather than storing older bytes over newer.
    if (!this.freshness.accept(slot, seq, stamped)) return slot;
    this.saves.writeSlotRaw(slot, stamped);
    return slot;
  }

  /** Unload-path durability: synchronously write the newest envelope this
   *  thread already holds. A save STARTED during pagehide needs a worker
   *  round-trip that races page teardown and usually loses; this flush never
   *  does. Content is as fresh as the last completed save - no-op when the
   *  interval save already landed the same bytes. */
  flushCachedSave(slot: number): boolean {
    if (!Number.isInteger(slot) || slot < 0 || slot >= 3) return false;
    const bytes = this.freshness.flushable(slot);
    if (bytes === null) return false;
    // Never throws into the unload handler chain; no false saved claim.
    return safeFlush(() => this.saves.writeSlotRawIfChanged(slot, bytes));
  }

  /** Load a local slot into the running sim. */
  async load(slot: number, nowMs?: number): Promise<{ tick: number; away?: OfflineCatchup }> {
    this.sessionGeneration += 1;
    this.freshness.invalidate();
    const envelope = this.readEnvelope(slot);
    if (envelope === null) throw new Error(`save: slot ${String(slot)} is empty`);
    const wait = this.next(['loaded', 'error'], 60_000);
    this.send({ kind: 'applyState', envelope, slot, ...(nowMs !== undefined ? { nowMs } : {}) });
    const res = await wait;
    if (res.kind === 'loaded') {
      return { tick: res.tick, ...(res.away ? { away: res.away } : {}) };
    }
    throw new Error(res.message);
  }

  deleteSlot(slot: number): void {
    this.sessionGeneration += 1;
    this.freshness.invalidate();
    this.saves.deleteSlot(slot);
  }

  /** Export a slot's envelope as portable JSON text (file download). */
  exportSlot(slot: number): string {
    const envelope = this.readEnvelope(slot);
    if (envelope === null) throw new Error(`save: slot ${String(slot)} is empty`);
    return envelope;
  }

  /** Read current live state for download only. Never writes or rotates a slot. */
  async exportCurrent(nowMs: number): Promise<string> {
    const wait = this.next(['stateData', 'error'], 30_000);
    this.send({kind:'save',slot:0});
    const res = await wait;
    if(res.kind==='error')throw new Error(res.message);
    return this.saves.stampEnvelope(res.envelope,nowMs);
  }

  /** Import an envelope (from a file) into the running sim. */
  async importEnvelope(envelope: string, nowMs?: number): Promise<{ tick: number; away?: OfflineCatchup }> {
    this.sessionGeneration += 1;
    this.freshness.invalidate();
    const wait = this.next(['loaded', 'error'], 60_000);
    this.send({ kind: 'applyState', envelope, slot: -1, ...(nowMs !== undefined ? { nowMs } : {}) });
    const res = await wait;
    if (res.kind === 'loaded') {
      return { tick: res.tick, ...(res.away ? { away: res.away } : {}) };
    }
    throw new Error(res.message);
  }

  stop(): void { this.send({ kind: 'stop' }); }
  terminate(): void { this.worker.terminate(); }

  /** Ordering + generation guard for anything the unload flush might write. */
  private readonly freshness = new SaveFreshness();

  private storeEnvelope(slot: number, envelope: string, nowMs?: number): void {
    // Wall-clock stamp happens here on the main thread: the worker never
    // sees Date.now (frozen sim contract), the envelope carries it instead.
    const stamped = nowMs !== undefined ? this.saves.stampEnvelope(envelope, nowMs) : envelope;
    this.saves.writeSlotRaw(slot, stamped);
    if (this.saves.readSlotRaw(slot) !== stamped) throw new Error('save: checkpoint readback failed');
    // Cache the STAMPED bytes so a flush never rewrites older content.
    this.freshness.accept(slot, this.freshness.issue(slot), stamped);
  }

  private readEnvelope(slot: number): string | null {
    if (!Number.isInteger(slot) || slot < 0 || slot >= 3) throw new Error(`save: bad slot ${String(slot)}`);
    return this.saves.readSlotRaw(slot);
  }
}
